import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Shield, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateIST } from '../utils/dateUtils';

const LoginLogsTab = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchUsername, setSearchUsername] = useState('');

  useEffect(() => {
    loadLogs();
  }, [filterStatus]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (searchUsername.trim()) {
        params.username = searchUsername.trim();
      }
      
      const response = await axiosInstance.get('/admin/login-logs', { params });
      setLogs(response.data.logs);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Failed to load login logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadLogs();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'restricted':
        return <Clock className="w-5 h-5 text-orange-600" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      default:
        return <Shield className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      restricted: 'bg-orange-100 text-orange-700',
      error: 'bg-red-100 text-red-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const stats = {
    total: logs.length,
    success: logs.filter(l => l.status === 'success').length,
    failed: logs.filter(l => l.status === 'failed').length,
    restricted: logs.filter(l => l.status === 'restricted').length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6" />
              Login Activity Logs
            </h2>
            <p className="text-blue-100 mt-1 text-sm">Monitor all login attempts and security events</p>
          </div>
          <button
            onClick={loadLogs}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white p-4 rounded-xl shadow-md border-2 border-gray-200">
          <p className="text-xs md:text-sm text-gray-600 font-semibold">Total Attempts</p>
          <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.total}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl shadow-md border-2 border-green-200">
          <p className="text-xs md:text-sm text-green-700 font-semibold">Successful</p>
          <p className="text-2xl md:text-3xl font-bold text-green-600 mt-1">{stats.success}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-xl shadow-md border-2 border-red-200">
          <p className="text-xs md:text-sm text-red-700 font-semibold">Failed</p>
          <p className="text-2xl md:text-3xl font-bold text-red-600 mt-1">{stats.failed}</p>
        </div>
        <div className="bg-orange-50 p-4 rounded-xl shadow-md border-2 border-orange-200">
          <p className="text-xs md:text-sm text-orange-700 font-semibold">Restricted</p>
          <p className="text-2xl md:text-3xl font-bold text-orange-600 mt-1">{stats.restricted}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md border-2 border-blue-200 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Status Filter */}
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Filter className="w-4 h-4 inline mr-1" />
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="restricted">Time Restricted</option>
              <option value="error">Error</option>
            </select>
          </div>

          {/* Username Search */}
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Search className="w-4 h-4 inline mr-1" />
              Search Username
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchUsername}
                onChange={(e) => setSearchUsername(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter username..."
                className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors text-sm"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Logs List - Mobile Cards / Desktop Table */}
      <div className="bg-white rounded-xl shadow-md border-2 border-blue-200 overflow-hidden">
        {/* Mobile Card View */}
        <div className="block md:hidden">
          {logs.map((log, index) => (
            <div key={index} className="p-4 border-b border-gray-200">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 pt-1">
                  {getStatusIcon(log.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 text-base truncate">{log.full_name}</p>
                      <p className="text-xs text-gray-500">@{log.username}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadge(log.status)}`}>
                      {log.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-600">
                      <span className="font-semibold">Role:</span> {log.role}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-semibold">Time:</span> {formatDateIST(log.login_time)}
                    </p>
                    {log.reason && (
                      <p className="text-gray-600">
                        <span className="font-semibold">Reason:</span> {log.reason}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Username</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Full Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Login Time</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.map((log, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(log.status)}`}>
                        {log.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-gray-800">{log.username}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-800">{log.full_name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{log.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{formatDateIST(log.login_time)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{log.reason || '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No login logs found</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <h4 className="font-semibold text-gray-800 mb-2">Status Legend:</h4>
        <ul className="space-y-1">
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <strong>Success:</strong> Login successful
          </li>
          <li className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-600" />
            <strong>Failed:</strong> Invalid username or password
          </li>
          <li className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-600" />
            <strong>Restricted:</strong> Login attempted outside allowed time window
          </li>
          <li className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <strong>Error:</strong> System error during login
          </li>
        </ul>
      </div>
    </div>
  );
};

export default LoginLogsTab;
